/**
 * Geocodificador especializado para infraestructuras industriales de Andalucía
 *
 * Conecta con WFS CDAV_02_tejido_economico (sustituto oficial de
 * DERA_g8_tejido_economico desde migración IECA 2026-03-18) para:
 * - Espacios productivos: polígonos industriales, parques empresariales
 * - Industria agroalimentaria: almazaras, bodegas, cooperativas
 * - Actividades extractivas: minas, canteras, graveras
 *
 * Cambios de nomenclatura CDAV (RESIL-WFS-S1):
 * - g8_01_EspacioProductivo      → cav_02_g08_01_EspacioProductivo
 * - g8_02_IndustriaAgroalimentaria → cav_02_g08_06_IndustriaAgroalimentaria
 * - g8_04_ActividadExtractiva    → cav_02_g08_07_ActividadExtractiva
 *
 * Fuentes oficiales:
 * - WFS CDAV G08: https://www.ideandalucia.es/services/CDAV_02_tejido_economico/wfs
 *
 * Tipología PTEL: INDUSTRIAL
 *
 * @module services/geocoding/specialized
 * @version 2.0.0 — RESIL-WFS-S1: migración DERA_g8 → CDAV_02_tejido_economico
 */

import type { 
  WFSFeature, 
  WFSSearchOptions 
} from './WFSBaseGeocoder';
import { 
  WFSBaseGeocoder 
} from './WFSBaseGeocoder';
import type { GeoJSONFeature } from '../../../types/geojson';
import type { SpecializedGeocoderConfig, GeocodingResult } from '../../../types/infrastructure';

/**
 * Tipos de infraestructuras industriales
 */
export enum IndustrialFacilityType {
  /** Polígono industrial / Parque empresarial */
  POLIGONO = 'POLIGONO',
  /** Almazara / Aceite */
  ALMAZARA = 'ALMAZARA',
  /** Bodega / Vino */
  BODEGA = 'BODEGA',
  /** Cooperativa agraria */
  COOPERATIVA = 'COOPERATIVA',
  /** Industria agroalimentaria genérica */
  AGROALIMENTARIA = 'AGROALIMENTARIA',
  /** Mina */
  MINA = 'MINA',
  /** Cantera */
  CANTERA = 'CANTERA',
  /** Gravera / Extracción áridos */
  GRAVERA = 'GRAVERA'
}

/**
 * Opciones de búsqueda específicas para infraestructuras industriales
 */
export interface IndustrialSearchOptions extends WFSSearchOptions {
  /** Tipo de infraestructura industrial */
  facilityType?: IndustrialFacilityType;
  /** Código de registro industrial */
  registryCode?: string;
  /** Actividad CNAE */
  cnaeCode?: string;
}

/**
 * Geocodificador especializado para infraestructuras industriales andaluzas
 * 
 * Precisión esperada: ±25-100m (polígonos son áreas extensas)
 * Cobertura: Tejido productivo completo de Andalucía
 * 
 * @example
 * ```typescript
 * const geocoder = new WFSIndustrialGeocoder();
 * const result = await geocoder.geocode({
 *   name: 'Polígono Industrial La Paz',
 *   municipality: 'Málaga',
 *   province: 'Málaga'
 * });
 * ```
 */
export class WFSIndustrialGeocoder extends WFSBaseGeocoder {
  
  // Mapeo de capas por tipo de infraestructura.
  // RESIL-WFS-S1: prefijo cav_02_ + reindexado de capas en CDAV.
  // g8_01 → g08_01 | g8_02 → g08_06 | g8_04 → g08_07
  private readonly LAYER_MAP: Record<string, string> = {
    'poligono':       'cav_02_g08_01_EspacioProductivo',
    'agroalimentaria': 'cav_02_g08_06_IndustriaAgroalimentaria',
    'extractiva':     'cav_02_g08_07_ActividadExtractiva',
  };

  /**
   * Configuración específica para WFS DERA G08 Tejido Económico
   */
  protected getDefaultConfig(): SpecializedGeocoderConfig {
    return {
      // RESIL-WFS-S1: endpoint canónico CDAV_02_tejido_economico (sustituye DERA_g8_tejido_economico)
      wfsEndpoint: 'https://www.ideandalucia.es/services/CDAV_02_tejido_economico/wfs',
      layerName: 'cav_02_g08_01_EspacioProductivo', // capa por defecto (polígonos)
      fuzzyThreshold: 0.35,
      timeout: 15000,
      outputSRS: 'EPSG:25830'
    };
  }

  /**
   * Parsea feature GeoJSON de DERA G08 Tejido Económico
   * 
   * Estructura esperada (varía según capa):
   * {
   *   type: "Feature",
   *   geometry: { type: "Point"|"MultiPoint"|"Polygon"|"MultiPolygon", coordinates: [...] },
   *   properties: {
   *     nombre: "Polígono Industrial La Paz",
   *     municipio: "Málaga",
   *     provincia: "Málaga",
   *     superficie: 125000,
   *     actividad: "...",
   *     ...
   *   }
   * }
   */
  protected parseFeature(feature: GeoJSONFeature): WFSFeature | null {
    try {
      const props = feature.properties || {};
      const geom = feature.geometry;

      // Validar geometría
      if (!geom || !geom.coordinates) {
        console.warn('Feature industrial sin geometría válida:', feature.id);
        return null;
      }

      // Manejar diferentes tipos de geometría
      let x: number, y: number;
      const coords = geom.coordinates as number[] | number[][] | number[][][];
      if (geom.type === 'Point' || geom.type === 'MultiPoint') {
        // Punto / MultiPoint(N=1) vía primitiva centralizada (sub-0a WMPF)
        const pt = this.parseGeometry(geom, `Industrial feature ${String(feature.id ?? 'sin-id')}`);
        if (!pt) return null;
        [x, y] = pt;
      } else if (geom.type === 'Polygon' && Array.isArray(coords) && coords.length > 0) {
        // Calcular centroide aproximado del polígono
        const ring = (coords as number[][][])[0];
        [x, y] = this.calculateCentroid(ring);
      } else if (geom.type === 'MultiPolygon' && Array.isArray(coords) && coords.length > 0) {
        // Usar primer polígono
        const ring = (coords as unknown as number[][][][])[0][0];
        [x, y] = this.calculateCentroid(ring);
      } else {
        console.warn('Geometría no soportada:', geom.type);
        return null;
      }

      // Validar coordenadas UTM30 Andalucía
      if (x < 100000 || x > 800000 || y < 4000000 || y > 4300000) {
        console.warn('Coordenadas fuera de rango UTM30:', x, y);
        return null;
      }

      // Extraer nombre (varios campos posibles según capa)
      const name = String(props.nombre || props.NOMBRE || 
                   props.denominacion || props.DENOMINACION ||
                   props.razon_social || props.RAZON_SOCIAL || '');

      return {
        name,
        x,
        y,
        municipality: String(props.municipio || props.MUNICIPIO || ''),
        province: String(props.provincia || props.PROVINCIA || ''),
        address: '', // Infraestructuras industriales normalmente no tienen dirección postal
        properties: {
          type: props.tipo || props.TIPO || '',
          activity: props.actividad || props.ACTIVIDAD || '',
          surface: props.superficie || props.SUPERFICIE || null,
          registryCode: props.codigo || props.CODIGO || '',
          category: props.categoria || props.CATEGORIA || '',
          ...props
        }
      };
    } catch (error) {
      console.error('Error parseando feature industrial:', error);
      return null;
    }
  }

  /**
   * Calcula centroide aproximado de un anillo de polígono
   */
  private calculateCentroid(ring: number[][]): [number, number] {
    if (!ring || ring.length === 0) return [0, 0];
    
    let sumX = 0, sumY = 0;
    for (const coord of ring) {
      sumX += coord[0];
      sumY += coord[1];
    }
    return [sumX / ring.length, sumY / ring.length];
  }

  /**
   * Construye filtro CQL específico para infraestructuras industriales
   */
  protected buildCQLFilter(options: IndustrialSearchOptions): string {
    const filters: string[] = [];

    // Filtro base
    const baseFilter = super.buildCQLFilter(options);
    if (baseFilter) {
      filters.push(baseFilter);
    }

    // Filtro por código de registro
    if (options.registryCode) {
      filters.push(`(codigo ILIKE '%${this.escapeCQL(options.registryCode)}%')`);
    }

    // Filtro por actividad CNAE
    if (options.cnaeCode) {
      filters.push(`(cnae ILIKE '%${this.escapeCQL(options.cnaeCode)}%' OR actividad ILIKE '%${this.escapeCQL(options.cnaeCode)}%')`);
    }

    return filters.length > 0 ? filters.join(' AND ') : '';
  }

  /**
   * Detecta tipo de infraestructura por nombre
   */
  private detectFacilityType(name: string): string {
    const nameLower = name.toLowerCase();
    
    // Actividades extractivas
    if (nameLower.includes('mina') || nameLower.includes('cantera') ||
        nameLower.includes('gravera') || nameLower.includes('extracción') ||
        nameLower.includes('áridos') || nameLower.includes('aridos')) {
      return 'cav_02_g08_07_ActividadExtractiva';
    }
    
    // Industria agroalimentaria
    if (nameLower.includes('almazara') || nameLower.includes('aceite') ||
        nameLower.includes('bodega') || nameLower.includes('cooperativa') ||
        nameLower.includes('conservera') || nameLower.includes('envasadora') ||
        nameLower.includes('matadero') || nameLower.includes('cárnica') ||
        nameLower.includes('láctea') || nameLower.includes('quesería')) {
      return 'cav_02_g08_06_IndustriaAgroalimentaria';
    }
    
    // Default: Espacios productivos (polígonos industriales)
    return 'cav_02_g08_01_EspacioProductivo';
  }

  /**
   * Geocodifica con selección automática de capa según nombre
   */
  public async geocodeWithAutoLayer(options: IndustrialSearchOptions): Promise<GeocodingResult | null> {
    // Detectar tipo y seleccionar capa
    const detectedLayer = this.detectFacilityType(options.name);
    this.config.layerName = detectedLayer;

    // Intentar con la capa detectada
    let result = await this.geocodeLegacy(options);
    
    if (result && result.confidence >= 70) {
      return result;
    }

    // Si no encuentra, probar otras capas
    const layersToTry = [
      'cav_02_g08_01_EspacioProductivo',
      'cav_02_g08_06_IndustriaAgroalimentaria',
      'cav_02_g08_07_ActividadExtractiva',
    ];
    
    for (const layer of layersToTry) {
      if (layer === detectedLayer) continue;
      
      this.config.layerName = layer;
      result = await this.geocodeLegacy(options);
      
      if (result && result.confidence >= 70) {
        return result;
      }
    }

    // Restaurar capa por defecto
    this.config.layerName = 'cav_02_g08_01_EspacioProductivo';
    return result;
  }

  /**
   * Obtiene todas las infraestructuras industriales de un municipio
   */
  public async getAllFacilitiesInMunicipality(
    municipality: string,
    province?: string
  ): Promise<WFSFeature[]> {
    const allFeatures: WFSFeature[] = [];
    const layers = [
      'cav_02_g08_01_EspacioProductivo',
      'cav_02_g08_06_IndustriaAgroalimentaria',
      'cav_02_g08_07_ActividadExtractiva',
    ];

    for (const layer of layers) {
      try {
        this.config.layerName = layer;
        
        const params = this.buildWFSParams({
          name: '',
          municipality,
          province,
          maxResults: 100
        });

        const response = await this.axiosInstance.get(this.config.wfsEndpoint, { params });
        const features = this.parseResponse(response.data);
        allFeatures.push(...features);

      } catch (error) {
        console.warn(`Error obteniendo ${layer} de ${municipality}:`, error);
      }
    }

    // Restaurar capa por defecto
    this.config.layerName = 'cav_02_g08_01_EspacioProductivo';
    return allFeatures;
  }
}

export default WFSIndustrialGeocoder;
